import { apiClient } from "./client";

// All SharePoint/Graph access now goes through the BFF (`/v3/sharepoint/*`),
// which exchanges the app's own bearer token for a delegated Graph token
// on-behalf-of the signed-in user. The frontend never talks to
// graph.microsoft.com directly and never holds a Graph-scoped token or the
// drive id — see "SharePoint document library" in knowledge_graph.md.

/**
 * The BFF serializes its Graph SDK model pass-throughs with their C#
 * PascalCase property names (`Id`, `WebUrl`, `Folder.ChildCount`, ...)
 * instead of the camelCase shape Graph's own REST API returns and this
 * module's types assume. Every JSON response from `/v3/sharepoint/*` is
 * normalized through this before use — lower-casing just the first
 * character of each key, recursively, which is all that separates the two
 * conventions here. Idempotent: a key that's already camelCase (or starts
 * with a non-letter, like `@microsoft.graph.downloadUrl`) passes through
 * unchanged, so this stays correct if the backend is ever fixed to emit
 * camelCase directly.
 */
const toCamelCaseKeys = <T>(value: unknown): T => {
  if (Array.isArray(value)) {
    return value.map((item) => toCamelCaseKeys(item)) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key.charAt(0).toLowerCase() + key.slice(1),
        toCamelCaseKeys(val),
      ]),
    ) as T;
  }
  return value as T;
};

export interface SharePointItem {
  id: string;
  name: string;
  folder?: {
    childCount: number;
  };
  file?: {
    mimeType: string;
  };
  size: number;
  lastModifiedDateTime: string;
  webUrl: string;
}

export interface SharePointItemVersion {
  id: string;
  "@microsoft.graph.downloadUrl": string;
  lastModifiedDateTime: string;
  size: number;
  lastModifiedBy: {
    user: {
      id: string;
      email: string;
      displayName: string;
    };
  };
}

export interface CreateFolderResponse {
  success: boolean;
  message?: string;
  error?: {
    code?: string;
    message?: string;
  };
  id?: string;
  name?: string;
  webUrl?: string;
}

export interface UploadSessionResponse {
  uploadUrl: string;
  expirationDateTime: string;
}

export const sharepointApi = {
  /**
   * Creates the project's whole folder tree in one call — batching and
   * idempotency (existing folders count as success) happen server-side now.
   * Safe to call again after a partial failure.
   */
  createFolders: async (
    projectName: string,
    opportunityNo?: string,
  ): Promise<CreateFolderResponse> => {
    try {
      const response = await apiClient.post<CreateFolderResponse>(
        "/v3/sharepoint/folders",
        {
          projectName: projectName.trim(),
          ...(opportunityNo && { opportunityNo }),
        },
      );
      return toCamelCaseKeys<CreateFolderResponse>(response.data);
    } catch (error: any) {
      console.error("SharePoint folder creation error:", error);
      const errorData = toCamelCaseKeys<CreateFolderResponse>(
        error.response?.data ?? {},
      );
      return {
        success: false,
        message: errorData.message || error.message || "Failed to create folder",
        error: {
          code: errorData.error?.code || "UnknownError",
          message: errorData.error?.message || error.message,
        },
      };
    }
  },

  /**
   * Resolves a project's SharePoint folder by name/opportunity number
   * directly, replacing the old paginate-root-and-search-by-name loop.
   * Returns null when the project has no folder yet (backend 404) rather
   * than throwing, since that's an expected outcome here.
   */
  resolveFolder: async (
    projectName: string,
    opportunityNo?: string,
  ): Promise<SharePointItem | null> => {
    try {
      const response = await apiClient.get<SharePointItem>(
        "/v3/sharepoint/folders/resolve",
        { params: { projectName, opportunityNo } },
      );
      return toCamelCaseKeys<SharePointItem>(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getItems: async (folderId: string): Promise<SharePointItem[]> => {
    const response = await apiClient.get<SharePointItem[]>(
      `/v3/sharepoint/items/${folderId}`,
    );
    return toCamelCaseKeys<SharePointItem[]>(response.data);
  },

  getItemVersions: async (
    itemId: string,
  ): Promise<SharePointItemVersion[]> => {
    const response = await apiClient.get<SharePointItemVersion[]>(
      `/v3/sharepoint/items/${itemId}/versions`,
    );
    return toCamelCaseKeys<SharePointItemVersion[]>(response.data);
  },

  getItemContent: async (itemId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/v3/sharepoint/items/${itemId}/content`,
      { responseType: "blob" },
    );
    return response.data;
  },

  getItemVersionContent: async (
    itemId: string,
    versionId: string,
  ): Promise<Blob> => {
    const response = await apiClient.get(
      `/v3/sharepoint/items/${itemId}/versions/${versionId}/content`,
      { responseType: "blob" },
    );
    return response.data;
  },

  /**
   * Every upload — regardless of file size — starts here. The returned
   * `uploadUrl` is a Graph upload-session URL, pre-authorized by Graph
   * itself: the file's bytes go straight from the browser to `uploadUrl`
   * and never pass through the BFF. Same-name uploads are auto-renamed by
   * Graph (`conflictBehavior: "rename"`) rather than overwritten.
   */
  createUploadSession: async (
    folderId: string,
    filename: string,
  ): Promise<UploadSessionResponse> => {
    const response = await apiClient.post<UploadSessionResponse>(
      `/v3/sharepoint/items/${folderId}/upload-session`,
      null,
      { params: { filename } },
    );
    return toCamelCaseKeys<UploadSessionResponse>(response.data);
  },
};
